import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { startSessionRefresh } from './utils/sessionRefresh';
import './styles/animations.css';
import Navbar from './components/Navbar';
import FairDetails from './components/FairDetails';
import Contact from './components/Contact';
import Feedback from './components/Feedback';
import Footer from './components/Footer';
import AdminLogin from './components/AdminLogin';
import UserLogin from './components/UserLogin';
import Signup from './components/Signup';
import EmailVerification from './components/EmailVerification';
import EmailSent from './components/EmailSent';
import AdminLayout from './components/AdminLayout';
import FairManagerLayout from './components/FairManagerLayout';
import AdminDashboard from './components/AdminDashboard';
// OwnerDashboard removed (Issue #146) - owner role deprecated
// Product management removed (Issue #83) - vendors manage their own products
// FairProductManagement removed (Issue #80) - products now belong to vendors
import ProductCategoryManagement from './components/ProductCategoryManagement';
import FeedbackManagement from './components/FeedbackManagement';
import PaymentTypeManagement from './components/PaymentTypeManagement';
// ProductUnitManagement removed (Issue #85) - product units system removed
// UserManagement removed (Issue #827) - replaced by RBAC Role Management at /admin/roles
import AdvertisementManagement from './components/AdvertisementManagement';
import FairForm from './components/FairForm';
import FeaturedFairDashboard from './components/FeaturedFairDashboard';
import PasswordReset from './components/PasswordReset';
import PasswordChange from './components/PasswordChange';
import AdminAuthGuard from './components/AdminAuthGuard';
import AdminOnlyGuard from './components/AdminOnlyGuard';
import AuthGuard from './components/AuthGuard';
import FairManagerAuthGuard from './components/FairManagerAuthGuard';
import VendorAuthGuard from './components/VendorAuthGuard';
import UserProfile from './components/UserProfile';
import PendingFairReview from './components/PendingFairReview';
import Welcome from './components/Welcome';
import IndexCard from './components/IndexCard';
import SlotSearchPage from './components/SlotSearchPage';
import VendorListPage from './components/VendorListPage';
import VendorProfileView from './components/VendorProfileView';
import VendorReviewPage from './components/VendorReviewPage';
import VendorDashboard from './components/VendorDashboard';
import VendorProfileForm from './components/VendorProfileForm';
import BoothPreferencesPage from './components/BoothPreferencesPage';
import VendorProductManagement from './components/VendorProductManagement';
import VendorProductForm from './components/VendorProductForm';
import VendorProductCatalog from './components/VendorProductCatalog';
import FairProductCatalogManager from './components/FairProductCatalogManager';
import VendorManagement from './components/VendorManagement';
import VendorRevenuePage from './pages/VendorRevenuePage';
import VendorExpensePage from './pages/VendorExpensePage';
import VendorROITrackerPage from './pages/VendorROITrackerPage';
import { VendorFinancialRedirect } from './components/VendorFinancialRedirect';
import AdminVendorProductManagement from './components/AdminVendorProductManagement';
import { VendorProvider } from './contexts/VendorContext';
import ReservationWizard from './components/ReservationWizard';
import VendorReservationsList from './components/VendorReservationsList';
import ReservationDetailsModal from './components/ReservationDetailsModal';
import ReservationConfirmation from './components/ReservationConfirmation';
import WaitlistManager from './components/WaitlistManager';
import RentalItemsManager from './components/RentalItemsManager';
import SlotPricingManager from './components/SlotPricingManager';
import WaitlistAnalytics from './components/WaitlistAnalytics';
import AdminAnalyticsDashboard from './components/admin/AdminAnalyticsDashboard';
import VendorWaitlistPage from './components/VendorWaitlistPage';
import AdPackageManagement from './components/AdPackageManagement';
import VendorAdPurchase from './components/VendorAdPurchase';
import VendorAdPurchaseHistory from './components/VendorAdPurchaseHistory';
import VendorAdvertisements from './components/VendorAdvertisements';
import VendorPreorders from './components/VendorPreorders';
import AdminAdPurchaseManagement from './components/AdminAdPurchaseManagement';
import FeatureFlagManagement from './components/admin/FeatureFlagManagement';
import EmailTemplateGallery from './components/EmailTemplateGallery';
import PreLaunchPage from './components/PreLaunchPage';
import PrelaunchSignupManagement from './components/PrelaunchSignupManagement';
import FairManagerRequestPage from './components/FairManagerRequestPage';
import AdminFairManagerRequests from './components/AdminFairManagerRequests';
// TEMPORARILY DISABLED - Issue causing white screen
// import ApplicationsStatusDashboard from './components/ApplicationsStatusDashboard';
import AdminManagerApplications from './components/AdminManagerApplications';
import AdminReservationManagement from './components/AdminReservationManagement';
import AdminReviewModeration from './components/AdminReviewModeration';
import FairManagerOnboarding from './components/FairManagerOnboarding';
import FairManagerDashboard from './components/FairManagerDashboard';
import FairManagerPayouts from './components/FairManagerPayouts';
import FairManagerFairForm from './components/FairManagerFairForm';
import FairManagerFairs from './components/FairManagerFairs';
import FairManagerReservations from './components/FairManagerReservations';
import FairManagerWaitlistPage from './components/FairManagerWaitlistPage';
import FairManagerPricingPage from './components/FairManagerPricingPage';
import FairManagerRentalsPage from './components/FairManagerRentalsPage';
import FairManagerSlotsPage from './components/FairManagerSlotsPage';
import FairManagerSlotManagement from './components/FairManagerSlotManagement';
import FairManagerStatistics from './components/FairManagerStatistics';
import CalendarView from './components/CalendarView';
import FairCalendar from './components/FairCalendar';
import VendorInquiryInbox from './components/VendorInquiryInbox';
import InquiryConversationView from './components/InquiryConversationView';
import FairManagerInquiryDashboard from './components/FairManagerInquiryDashboard';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import AdminBlogManager from './components/AdminBlogManager';
import FairManagerInquiryConversation from './components/FairManagerInquiryConversation';
import EmailCampaignManagement from './components/EmailCampaignManagement';
import EmailCampaignAnalyticsPage from './components/EmailCampaignAnalyticsPage';
import PostFairSurvey from './components/PostFairSurvey';
import VendorSatisfactionTrends from './components/VendorSatisfactionTrends';
import FairManagerFeedbackDashboard from './components/FairManagerFeedbackDashboard';
import AdminRoleManagement from './components/AdminRoleManagement';
import AdminUserManagement from './components/AdminUserManagement';
// Issue #966 - Phase 1: Fair Manager Missing Endpoints UI
import FairSubmissionPayment from './components/FairSubmissionPayment';
import FairManagerEmailCampaigns from './components/FairManagerEmailCampaigns';
// Issue #966 - Phase 2: Fair Manager Booth Management & Availability UI (Issue #1023)
import SlotAvailabilityMatrix from './components/SlotAvailabilityMatrix';
import BoothLayoutManager from './components/BoothLayoutManager';
import BoothAmenitiesManager from './components/BoothAmenitiesManager';
// Issue #1076 - Phase 3 of Issue #966: Contact Settings Management
import FairManagerContactSettings from './components/FairManagerContactSettings';
// Issue #1078: Interactive Booth Map for vendors
import InteractiveBoothMapWrapper from './components/InteractiveBoothMapWrapper';
// Issue #1310: Privacy Policy and Cookie Consent for GDPR/CCPA Compliance
import PrivacyPolicy from './components/PrivacyPolicy';
import CookiePolicy from './components/CookiePolicy';
import TermsOfService from './components/TermsOfService';
import CookieConsentBanner from './components/CookieConsentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import BlogErrorFallback from './components/BlogErrorFallback';
// Issue #1513: Configurable landing page with calendar option
import ConfigurableLandingPage from './components/ConfigurableLandingPage';
import { initCsrfToken, API_BASE_URL, featureFlagApi } from './services/api';
import { analytics } from './services/analytics';
import { extractApiError } from './utils/errorUtils';
import { FeatureFlag } from './hooks/useFeatureFlag';

export default function App() {
  // State for prelaunch mode and loading
  const [isPrelaunchMode, setIsPrelaunchMode] = useState<boolean | null>(null);
  const [isLoadingPrelaunchFlag, setIsLoadingPrelaunchFlag] = useState(true);

  // Initialize CSRF token and fetch prelaunch mode flag on app mount
  useEffect(() => {
    // Fetch prelaunch_mode feature flag from database (Issue #176)
    const initializeApp = async () => {
      try {
        // Initialize CSRF token first (required for API calls)
        if (import.meta.env.DEV) {
          console.log('[App] Initializing CSRF token...');
        }
        await initCsrfToken();
        if (import.meta.env.DEV) {
          const csrfToken = localStorage.getItem('csrf_token');
          console.log('[App] CSRF token initialized:', csrfToken ? `${csrfToken.substring(0, 20)}...` : 'NOT FOUND');
        }

        // Initialize analytics (Issue #498)
        analytics.initialize();

        // Fetch feature flag after CSRF token is ready
        const response = await featureFlagApi.checkFlag('prelaunch_mode');
        setIsPrelaunchMode(response.is_enabled);
      } catch (error: unknown) {
        const apiError = extractApiError(error);
        console.error('Failed to fetch prelaunch_mode flag:', apiError);
        // Default to false on error to show full application
        setIsPrelaunchMode(false);
      } finally {
        setIsLoadingPrelaunchFlag(false);
      }
    };

    initializeApp();
  }, []);

  // Initialize session refresh for RBAC role updates (Issue #860)
  useEffect(() => {
    // Start periodic session refresh to keep user roles up-to-date
    const stopRefresh = startSessionRefresh();

    // Cleanup: stop refresh when component unmounts
    return () => {
      if (typeof stopRefresh === 'function') {
        stopRefresh();
      }
    };
  }, []);

  // Show loading spinner while fetching feature flag
  if (isLoadingPrelaunchFlag) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center" role="status" aria-live="polite">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"
            aria-label="Loading application"
          ></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If prelaunch mode is enabled, show landing page with auth routes accessible
  // Auth routes (/login, /reset-password, etc.) and admin routes remain accessible (Issue #330)
  if (isPrelaunchMode === true) {
    return (
      <HelmetProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              maxWidth: '500px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <Router>
          <Routes>
            {/* Authentication routes accessible during prelaunch (Issue #330) */}
            <Route path="/login" element={<UserLogin />} />
            <Route path="/reset-password" element={<PasswordReset onBack={() => window.history.back()} />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/email-sent" element={<EmailSent />} />

            {/* Fair Manager Onboarding (Issue #1455) - Public route with token authentication */}
            <Route path="/onboarding/:token" element={<FairManagerOnboarding />} />

            {/* Admin routes accessible during prelaunch (Issue #330) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<PasswordReset onBack={() => window.history.back()} />} />
            <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
              <Route path="dashboard" element={<AdminOnlyGuard><AdminDashboard /></AdminOnlyGuard>} />
              <Route path="product-categories" element={<AdminOnlyGuard><ProductCategoryManagement /></AdminOnlyGuard>} />
              <Route path="payment-types" element={<AdminOnlyGuard><PaymentTypeManagement apiBaseUrl={API_BASE_URL} /></AdminOnlyGuard>} />
              <Route path="users" element={<AdminOnlyGuard><AdminUserManagement /></AdminOnlyGuard>} />
              <Route path="vendors" element={<AdminOnlyGuard><VendorManagement /></AdminOnlyGuard>} />
              <Route path="vendors/:vendorId/products" element={<AdminOnlyGuard><AdminVendorProductManagement /></AdminOnlyGuard>} />
              <Route path="advertisements" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdvertisementManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="ad-packages" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdPackageManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="ad-purchases" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdminAdPurchaseManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="feedback" element={<AdminOnlyGuard><FeedbackManagement /></AdminOnlyGuard>} />
              <Route path="featured-fairs" element={<AdminOnlyGuard><FeaturedFairDashboard /></AdminOnlyGuard>} />
              <Route path="feature-flags" element={<AdminOnlyGuard><FeatureFlagManagement /></AdminOnlyGuard>} />
              <Route path="dev/email-templates" element={<AdminOnlyGuard><EmailTemplateGallery /></AdminOnlyGuard>} />
              <Route path="prelaunch-signups" element={<AdminOnlyGuard><PrelaunchSignupManagement /></AdminOnlyGuard>} />
              <Route path="waitlist-analytics" element={<AdminOnlyGuard><WaitlistAnalytics /></AdminOnlyGuard>} />
              <Route path="analytics" element={<AdminOnlyGuard><AdminAnalyticsDashboard /></AdminOnlyGuard>} />
              <Route path="fair-manager-requests" element={<AdminOnlyGuard><AdminFairManagerRequests /></AdminOnlyGuard>} />
              <Route path="manager-applications" element={<AdminOnlyGuard><AdminManagerApplications /></AdminOnlyGuard>} />
              <Route path="reservations" element={<AdminOnlyGuard><AdminReservationManagement /></AdminOnlyGuard>} />
              <Route path="reviews" element={<AdminOnlyGuard><AdminReviewModeration /></AdminOnlyGuard>} />
              <Route path="pending-fairs" element={<AdminOnlyGuard><PendingFairReview /></AdminOnlyGuard>} />
              <Route path="roles" element={<AdminOnlyGuard><AdminRoleManagement /></AdminOnlyGuard>} />
              <Route path="blog" element={
                <AdminOnlyGuard>
                  <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                    <AdminBlogManager />
                  </ErrorBoundary>
                </AdminOnlyGuard>
              } />
              <Route path="fairs/new" element={<AdminOnlyGuard><FairForm /></AdminOnlyGuard>} />
              <Route path="fairs/:fairId/edit" element={<AdminOnlyGuard><FairForm isEdit={true} /></AdminOnlyGuard>} />
              <Route path="fair/:fairId" element={<FairForm isEdit={true} />} />
            </Route>

            {/* Blog routes - gated by blog_enabled feature flag (Issue #1466) */}
            <Route path="/blog" element={
              <FeatureFlag flagKey="blog_enabled" fallback={<Navigate to="/" replace />}>
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                      <BlogList />
                    </ErrorBoundary>
                  </main>
                  <Footer />
                </div>
              </FeatureFlag>
            } />
            <Route path="/blog/:slug" element={
              <FeatureFlag flagKey="blog_enabled" fallback={<Navigate to="/" replace />}>
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                      <BlogPost />
                    </ErrorBoundary>
                  </main>
                  <Footer />
                </div>
              </FeatureFlag>
            } />

            {/* All other routes redirect to prelaunch page */}
            <Route path="*" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <PreLaunchPage />
                </main>
                <Footer />
              </div>
            } />
          </Routes>
        </Router>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      {/* Splash Screen - Issue #1325 - TEMP DISABLED due to white screen issue */}
      {/* <SplashScreenErrorBoundary>
        <SplashScreen />
      </SplashScreenErrorBoundary> */}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            maxWidth: '500px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Router>
        {/* Cookie Consent Banner - shows on first visit (Issue #1310) */}
        <CookieConsentBanner />
        <div className="min-h-screen bg-background">
          <Routes>
            {/* User Login Routes */}
            <Route path="/login" element={<UserLogin />} />
            <Route path="/reset-password" element={<PasswordReset onBack={() => window.history.back()} />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/email-sent" element={<EmailSent />} />

            {/* Authenticated User Routes (all logged-in users) */}
            <Route path="/profile" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard><UserProfile /></AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            {/* TEMPORARILY DISABLED - Issue causing white screen */}
            {/* <Route path="/my-applications" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <div className="container mx-auto px-4 py-8">
                    <AuthGuard><ApplicationsStatusDashboard /></AuthGuard>
                  </div>
                </main>
                <Footer />
              </div>
            } /> */}
            <Route path="/change-password" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard><PasswordChange onSuccess={() => window.history.back()} onCancel={() => window.history.back()} /></AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/fair-manager-request" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard><FairManagerRequestPage /></AuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Dashboard Routes (Authenticated) */}
            <Route path="/vendor/dashboard" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorDashboard />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendor/profile/new" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProfileForm />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendor/profile/:vendorId/edit" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProfileForm isEdit={true} />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Booth Preferences (Issue #677) */}
            <Route path="/vendor/:vendorId/booth-preferences" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorProvider>
                    <BoothPreferencesPage />
                  </VendorProvider>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Product Management Routes (Authenticated, Issue #83) */}
            <Route path="/vendor/products" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProductManagement />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendor/products/new" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProductForm />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendor/products/:productId/edit" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProductForm />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Product Catalog Manager (Issue #1177) */}
            <Route path="/vendor/:vendorId/product-catalog" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <VendorProductCatalog />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Financial Tracking Routes (Authenticated, Issue #1160, Fixed #1173, Feature Flag #1474) */}
            <Route path="/vendor/fairs/:fairId/revenue" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorRevenuePage />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/fairs/:fairId/expenses" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorExpensePage />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/fairs/:fairId/roi-tracker" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorROITrackerPage />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />

              {/* Backward Compatibility Redirects (Issue #1173 Enhancement) */}
              <Route path="/vendor/revenue" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorFinancialRedirect basePath="revenue" />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/expenses" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorFinancialRedirect basePath="expenses" />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/roi-tracker" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorFinancialRedirect basePath="roi-tracker" />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />

            {/* Vendor Fair Product Catalog Route (Authenticated, Issue #701) */}
            <Route path="/vendor/fair-catalog" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <FairProductCatalogManager />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Waitlist Route (Authenticated) - Issue #336: Changed from VendorAuthGuard to AuthGuard */}
            <Route path="/vendor/waitlist" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <VendorWaitlistPage />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Post-Fair Survey (Authenticated, Issue #682) */}
            <Route path="/vendors/:vendorId/fairs/:fairId/survey" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorAuthGuard>
                    <VendorProvider>
                      <PostFairSurvey />
                    </VendorProvider>
                  </VendorAuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Vendor Satisfaction Trends (Authenticated, Issue #682, Feature Flag #1473) */}
            <Route path="/vendors/:vendorId/analytics/satisfaction-trends" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorProvider>
                        <VendorSatisfactionTrends />
                      </VendorProvider>
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />

            {/* Vendor Ad Purchase Routes (Authenticated, Issue #86) */}
            <Route path="/vendor/ad-purchase" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorProvider>
                        <VendorAdPurchase />
                      </VendorProvider>
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/ad-purchases" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorAdPurchaseHistory />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />
              <Route path="/vendor/advertisements" element={
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorAdvertisements />
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              } />

            {/* Vendor Pre-Orders Route (Authenticated, Issue #675, #1158, #1468) */}
            <Route path="/vendor/preorders" element={
              <FeatureFlag flagKey="preorders_enabled" fallback={<Navigate to="/" replace />}>
                <div className="min-h-screen flex flex-col bg-background">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <VendorAuthGuard>
                      <VendorProvider>
                        <VendorPreorders />
                      </VendorProvider>
                    </VendorAuthGuard>
                  </main>
                  <Footer />
                </div>
              </FeatureFlag>
              } />

            {/* Vendor Inquiry Routes (Authenticated, Issue #377) */}
            <Route path="/vendor/inquiries" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <VendorInquiryInbox />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendor/inquiries/:inquiryId" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <InquiryConversationView />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Fair Manager Routes (Authenticated, Issue #369, #372, #377) - Uses FairManagerLayout with sidebar */}
            <Route path="/fairmanager" element={
              <FairManagerAuthGuard>
                <FairManagerLayout />
              </FairManagerAuthGuard>
            }>
              {/* Top-level Fair Manager pages */}
              <Route path="dashboard" element={<FairManagerDashboard />} />
              <Route path="fairs" element={<FairManagerFairs />} />
              <Route path="fairs/create" element={<FairManagerFairForm />} />
              <Route path="fairs/:fairId/edit" element={<FairManagerFairForm />} />
              <Route path="fairs/:fairId/slots" element={<FairManagerSlotManagement />} />
              <Route path="payouts" element={<FairManagerPayouts />} />
              <Route path="reservations" element={<FairManagerReservations />} />
              <Route path="waitlist" element={<FairManagerWaitlistPage />} />
              <Route path="pricing" element={
                <FeatureFlag
                  flagKey="fair_manager_pricing_template"
                  fallback={<div className="p-4">This feature is not yet available.</div>}
                >
                  <FairManagerPricingPage />
                </FeatureFlag>
              } />
              <Route path="rentals" element={<FairManagerRentalsPage />} />
              <Route path="slots" element={<FairManagerSlotsPage />} />
              <Route path="statistics" element={<FairManagerStatistics />} />
              <Route path="fair/new" element={<FairManagerFairForm />} />

              {/* Fair-specific management routes (require fairId) */}
              <Route path="fairs/:fairId/vendor-feedback" element={<FairManagerFeedbackDashboard />} />

              {/* Shared management components - Fair Managers access via /fairmanager, Admins via /admin (Issue #1082) */}
              <Route path="fair/:fairId/pricing" element={<SlotPricingManager />} />
              <Route path="fair/:fairId/rentals" element={<RentalItemsManager />} />
              <Route path="fair/:fairId/waitlist" element={<WaitlistManager />} />

              {/* Fair Manager Inquiry Routes (Issue #377) */}
              <Route path="inquiries" element={<FairManagerInquiryDashboard />} />
              <Route path="inquiries/:inquiryId" element={<FairManagerInquiryConversation />} />
              <Route path="campaigns" element={<EmailCampaignManagement />} />
              <Route path="campaigns/:campaignId/analytics" element={<EmailCampaignAnalyticsPage />} />

              {/* Issue #966 - Phase 1: Fair Manager Missing Endpoints UI */}
              <Route path="fairs/:fairId/submission-payment" element={<FairSubmissionPayment />} />
              <Route path="fairs/:fairId/email-campaigns" element={<FairManagerEmailCampaigns />} />

              {/* Issue #966 - Phase 2: Fair Manager Booth Management & Availability UI (Issue #1023) */}
              <Route path="fairs/:fairId/availability" element={<SlotAvailabilityMatrix />} />
              <Route path="fairs/:fairId/booth-layouts" element={<BoothLayoutManager />} />
              <Route path="fairs/:fairId/amenities" element={<BoothAmenitiesManager />} />

              {/* Issue #1076 - Phase 3 of Issue #966: Contact Settings Management */}
              <Route path="fairs/:fairId/contact-settings" element={<FairManagerContactSettings />} />
            </Route>

            {/* Reservation Routes (Authenticated) */}
            <Route path="/slots/:slotId/reserve" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <VendorProvider>
                      <ReservationWizard />
                    </VendorProvider>
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/reservations" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <VendorReservationsList />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/reservations/:reservationId" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <ReservationDetailsModal />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/reservation-confirmed" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <AuthGuard>
                    <ReservationConfirmation />
                  </AuthGuard>
                </main>
                <Footer />
              </div>
            } />

            {/* Public Auth Routes */}
            <Route path="/signup" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <Signup />
                </main>
                <Footer />
              </div>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<PasswordReset onBack={() => window.history.back()} />} />
            <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
              {/* Admin-only routes */}
              <Route path="dashboard" element={<AdminOnlyGuard><AdminDashboard /></AdminOnlyGuard>} />
              {/* Product management removed (Issue #83) - vendors manage their own products */}
              {/* Issue #173: Removed apiBaseUrl prop, component now uses centralized API client */}
              <Route path="product-categories" element={<AdminOnlyGuard><ProductCategoryManagement /></AdminOnlyGuard>} />
              <Route path="payment-types" element={<AdminOnlyGuard><PaymentTypeManagement apiBaseUrl={API_BASE_URL} /></AdminOnlyGuard>} />
              {/* Product units route removed (Issue #85) - product units system removed */}
              {/* Fair categories removed (Issue #84) - feature not being used */}
              {/* users route removed (Issue #827) - use /admin/roles for RBAC user management */}
              <Route path="vendors" element={<AdminOnlyGuard><VendorManagement /></AdminOnlyGuard>} />
              <Route path="vendors/:vendorId/products" element={<AdminOnlyGuard><AdminVendorProductManagement /></AdminOnlyGuard>} />
              <Route path="advertisements" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdvertisementManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="ad-packages" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdPackageManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="ad-purchases" element={
                <AdminOnlyGuard>
                  <FeatureFlag flagKey="vendor_advertisements" fallback={<div className="p-4">This feature is not enabled.</div>}>
                    <AdminAdPurchaseManagement />
                  </FeatureFlag>
                </AdminOnlyGuard>
              } />
              <Route path="feedback" element={<AdminOnlyGuard><FeedbackManagement /></AdminOnlyGuard>} />
              <Route path="featured-fairs" element={<AdminOnlyGuard><FeaturedFairDashboard /></AdminOnlyGuard>} />
              <Route path="feature-flags" element={<AdminOnlyGuard><FeatureFlagManagement /></AdminOnlyGuard>} />
              <Route path="dev/email-templates" element={<AdminOnlyGuard><EmailTemplateGallery /></AdminOnlyGuard>} />
              <Route path="prelaunch-signups" element={<AdminOnlyGuard><PrelaunchSignupManagement /></AdminOnlyGuard>} />
              <Route path="waitlist-analytics" element={<AdminOnlyGuard><WaitlistAnalytics /></AdminOnlyGuard>} />
              <Route path="analytics" element={<AdminOnlyGuard><AdminAnalyticsDashboard /></AdminOnlyGuard>} />
              <Route path="fair-manager-requests" element={<AdminOnlyGuard><AdminFairManagerRequests /></AdminOnlyGuard>} />
              <Route path="manager-applications" element={<AdminOnlyGuard><AdminManagerApplications /></AdminOnlyGuard>} />
              <Route path="reservations" element={<AdminOnlyGuard><AdminReservationManagement /></AdminOnlyGuard>} />
              <Route path="reviews" element={<AdminOnlyGuard><AdminReviewModeration /></AdminOnlyGuard>} />
              <Route path="pending-fairs" element={<AdminOnlyGuard><PendingFairReview /></AdminOnlyGuard>} />
              <Route path="roles" element={<AdminOnlyGuard><AdminRoleManagement /></AdminOnlyGuard>} />
              <Route path="blog" element={
                <AdminOnlyGuard>
                  <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                    <AdminBlogManager />
                  </ErrorBoundary>
                </AdminOnlyGuard>
              } />
              <Route path="fairs/new" element={<AdminOnlyGuard><FairForm /></AdminOnlyGuard>} />
              <Route path="fairs/:fairId/edit" element={<AdminOnlyGuard><FairForm isEdit={true} /></AdminOnlyGuard>} />

              {/* Owner routes removed (Issue #146) - owner role deprecated */}
              <Route path="fair/:fairId" element={<FairForm isEdit={true} />} />
              {/* fair/:fairId/products route removed (Issue #80) - products managed by vendors */}

              {/* Shared management components - Admins access via /admin, Fair Managers via /fairmanager (Issue #1082) */}
              <Route path="fair/:fairId/waitlist" element={<WaitlistManager />} />
              <Route path="fair/:fairId/rentals" element={<RentalItemsManager />} />
              <Route path="fair/:fairId/pricing" element={<SlotPricingManager />} />

              {/* User profile and password routes */}
              <Route path="profile" element={<UserProfile />} />
              <Route path="change-password" element={<PasswordChange onSuccess={() => window.history.back()} onCancel={() => window.history.back()} />} />
            </Route>

            {/* Welcome and Index Card Routes */}
            <Route path="welcome" element={
              <div className="min-h-screen flex flex-col bg-background">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <Welcome />
                </main>
                <Footer />
              </div>
            } />
            <Route path="index-card" element={<IndexCard />} />

            {/* Public Routes */}
            {/* Issue #1513: Configurable landing page (calendar/listing/marketing) */}
            <Route path="/" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <ConfigurableLandingPage />
                <Footer />
              </div>
            } />
            {/* Marketing/About page - accessible at /about */}
            <Route path="/about" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <Welcome />
                <Footer />
              </div>
            } />
            <Route path="/fair/:fairId" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <div className="container mx-auto px-4 py-8">
                    <FairDetails />
                  </div>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/fairs/calendar" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <FairCalendar />
                </main>
                <Footer />
              </div>
            } />
            <Route path="/fairs/:fairId/slots" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <SlotSearchPage />
                </main>
                <Footer />
              </div>
            } />
            <Route path="/fairs/:fairId/booth-map" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <div className="container mx-auto px-4 py-8">
                    <InteractiveBoothMapWrapper />
                  </div>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/contact" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <div className="container mx-auto px-4 py-8">
                    <Contact />
                  </div>
                </main>
                <Footer />
              </div>
            } />
            <Route path="/feedback" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <div className="container mx-auto px-4 py-8">
                    <Feedback />
                  </div>
                </main>
                <Footer />
              </div>
            } />
            {/* Privacy & Legal Routes (Issue #1310) */}
            <Route path="/privacy" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <PrivacyPolicy />
                <Footer />
              </div>
            } />
            <Route path="/cookies" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <CookiePolicy />
                <Footer />
              </div>
            } />
            <Route path="/terms" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <TermsOfService />
                <Footer />
              </div>
            } />
            {/* Public Vendor Routes */}
            <Route path="/vendors" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorListPage />
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendors/:vendorId" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorProfileView />
                </main>
                <Footer />
              </div>
            } />
            <Route path="/vendors/:vendorId/review" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <VendorReviewPage />
                </main>
                <Footer />
              </div>
            } />
            {/* Calendar View Route (Issue #211) */}
            <Route path="/calendar" element={
              <div className="h-screen flex flex-col overflow-hidden">
                <Navbar />
                <main className="flex-grow overflow-y-auto">
                  <CalendarView />
                </main>
                <Footer />
              </div>
            } />
            {/* Blog Routes (Issue #1386, #1401, #1466) - Public access gated by blog_enabled feature flag */}
            <Route path="/blog" element={
              <FeatureFlag flagKey="blog_enabled" fallback={<Navigate to="/" replace />}>
                <div className="h-screen flex flex-col overflow-hidden">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                      <BlogList />
                    </ErrorBoundary>
                  </main>
                  <Footer />
                </div>
              </FeatureFlag>
            } />
            <Route path="/blog/:slug" element={
              <FeatureFlag flagKey="blog_enabled" fallback={<Navigate to="/" replace />}>
                <div className="h-screen flex flex-col overflow-hidden">
                  <Navbar />
                  <main className="flex-grow overflow-y-auto">
                    <ErrorBoundary fallback={(error, resetError) => <BlogErrorFallback error={error} resetError={resetError} />}>
                      <BlogPost />
                    </ErrorBoundary>
                  </main>
                  <Footer />
                </div>
              </FeatureFlag>
            } />
          </Routes>
        </div>
      </Router>
    </HelmetProvider>
  );
}